import React, { useEffect } from "react";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import {
  CustomButtonWithBorder,
  CustomButton,
} from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  doubleArrowStyle,
  themes,
  parentBoxStyle,
  childBoxStyleWithColor,
  infoStyle,
  toAlignEnd,
  defaultAllSelection,
  childBoxStyle,
  nameLength,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { PaymentMode } from "pages/PayRoll/PayRollHeaders";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { CustomMultiSelection } from "components/CustomComponents/CustomMultiSelection";

export const CreatePayrollGroup = ({ onClose, reCallApi, setHanldeLoader }) => {
  const { topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      is_auto_name: false,
      payrun_cycle: "",
      payment_bank: "",
      mode_of_payment: "",
      name: "",
      company_list: [parseInt(topbarCompanyId)],
    },
  });
  const selectedCompanies = watch("company_list") || [];
  let companyDetails = localStorage.getItem("companyAccess")
    ? JSON.parse(localStorage.getItem("companyAccess"))
    : [];
  const company_ids = selectedCompanies.includes(defaultAllSelection)
    ? companyDetails
        .filter((c) => c.company_id !== defaultAllSelection)
        .map((c) => c.company_id)
    : selectedCompanies;
  const { domain } = useSelector((state) => state.tokenReducer);
  const { result: payrunCycleData, setLoad: reCallPayRunApi } = useFetch(
    `${domain}payroll/setup/payruncycle/pay-run-cycles/?company_ids=[${company_ids}]`
  );

  const { result: bankDetailsData, setLoad: reCallPaymentBankApi } = useFetch(
    `${domain}payroll/setup/paymentbank/payment-bank/?company_ids=[${company_ids}]`
  );

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    delete data.company_list;
    let payload = { ...data, users: [], company_ids: company_ids };
    const response = await customAPI(
      `${domain}payroll/setup/payrollgroup/payroll-group/`,
      "POST",
      payload
    );
    if (response.ok) {
      showToast("success", "Payroll group created.");
      setValue("button_loader", false);
      onClose();
      setHanldeLoader(false);
      reCallApi(true);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const handleCycleName = (event, data) => {
    if (data) {
      setValue("payrun_cycle", data?.name);
      setValue("pay_run_cycle", data.id);
      setValue("is_auto_name", true);
    } else {
      setValue("payrun_cycle", "");
      setValue("pay_run_cycle", "");
    }
  };

  const handlePaymentBank = (event, data) => {
    if (data) {
      setValue("payment_bank", data?.bank_name);
      setValue("source_bank_detail", data?.id);
      setValue("is_auto_name", true);
    } else {
      setValue("payment_bank", "");
      setValue("source_bank_detail", "");
    }
  };

  const handlePaymentMode = (event, data) => {
    if (data) {
      setValue("mode_of_payment", data.label);
      setValue("is_auto_name", true);
    } else {
      setValue("mode_of_payment", "");
    }
  };

  const handlePayrollGroupName = (event) => {
    if (event.target.value.length <= nameLength) {
      setValue("name", event.target.value);
      setValue("is_auto_name", false);
    }
  };

  const isAutoName = watch("is_auto_name");
  const payrunCycle = watch("payrun_cycle");
  const paymentBank = watch("payment_bank");
  const paymentMode = watch("mode_of_payment");

  useEffect(() => {
    if (isAutoName) {
      const autoName = `${payrunCycle || ""}-${paymentBank || ""}-${
        paymentMode || ""
      }`;
      setValue("name", autoName);
    }
  }, [isAutoName, payrunCycle, paymentBank, paymentMode, setValue]);

  return (
    <Box sx={parentBoxStyle}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box sx={childBoxStyleWithColor}>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={onClose} sx={doubleArrowStyle}>
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Create Payroll Group
            </Typography>
            <Typography {...infoStyle}>
              Group employees based on their pay schedule, payment method, and
              bank to simplify payroll processing
            </Typography>
          </Box>
        </Box>
      </Paper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={childBoxStyle}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Company Name</Typography>
              <Typography {...infoStyle}>
                Select the company for which this payrun cycle applies
              </Typography>
              <Controller
                name="company_list"
                control={control}
                rules={{ required: "Select at least one company" }}
                render={({ field, fieldState: { error } }) => (
                  <CustomMultiSelection
                    value={field.value || []}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      reCallPayRunApi(true);
                      reCallPaymentBankApi(true);
                      setValue("payrun_cycle", "");
                      setValue("payment_bank", "");
                    }}
                    options={companyDetails || []}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Payrun Cycle</Typography>
              <Typography {...infoStyle}>
                Choose an existing pay schedule to define when salaries are
                processed.
              </Typography>
              <CustomAutoComplete
                dataList={payrunCycleData?.map((obj) => ({
                  ...obj,
                  label: obj?.name,
                }))}
                rules={{
                  ...register("payrun_cycle", {
                    required: "Payrun cycle is required",
                  }),
                }}
                selectedValue={watch("payrun_cycle")}
                updateDataFunction={handleCycleName}
                placeHolder="Select a pay schedule (e.g., Weekly – Monday, Monthly – 10th Day)"
                errorMessage={
                  errors?.payrun_cycle?.message
                    ? errors?.payrun_cycle?.message
                    : ""
                }
                textBoxError={errors?.payrun_cycle ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Payment Bank</Typography>
              <Typography {...infoStyle}>
                Select the bank account through which salaries for this group
                will be disbursed.
              </Typography>
              <CustomAutoComplete
                dataList={bankDetailsData?.map((obj) => ({
                  ...obj,
                  label: obj?.bank_name,
                }))}
                rules={{
                  ...register("payment_bank", {
                    required: "Payment bank is required",
                  }),
                }}
                selectedValue={watch("payment_bank")}
                updateDataFunction={handlePaymentBank}
                placeHolder="Select a bank (e.g., SBI, HDFC, ICICI)"
                errorMessage={
                  errors?.payment_bank?.message
                    ? errors?.payment_bank?.message
                    : ""
                }
                textBoxError={errors?.payment_bank ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Payment Mode</Typography>
              <Typography {...infoStyle}>
                Choose how employees in this group will receive their salary
                payments.
              </Typography>
              <CustomAutoComplete
                dataList={PaymentMode}
                rules={{
                  ...register("mode_of_payment", {
                    required: "Payment mode is required",
                  }),
                }}
                selectedValue={watch("mode_of_payment")}
                updateDataFunction={handlePaymentMode}
                placeHolder="Select a payment method (e.g., Cash, Bank Transfer, Cheque)"
                errorMessage={
                  errors?.mode_of_payment?.message
                    ? errors?.mode_of_payment?.message
                    : ""
                }
                textBoxError={errors?.mode_of_payment ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Payroll Group Name 
              </Typography>
              <Typography {...infoStyle}>
                Enter a name to identify this payroll group easily during
                processing.
              </Typography>
              <CustomTextBox
                selectedValue={watch("name")}
                rules={{
                  ...register("name", {
                    required: "Payroll group name is required",
                    minLength: {
                      value: 3,
                      message: "Minium length is 3 characters",
                    },
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={handlePayrollGroupName}
                placeHolder="Enter group name (e.g., Weekly Staff, Monthly Contract Team)"
                errorMessage={
                  errors?.name?.message ? errors?.name?.message : ""
                }
                textBoxError={errors.name ? true : false}
              />
            </Box>
            <Box sx={toAlignEnd}>
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionFuntion={handleSubmit(onSubmit)}
                actionName="Create"
                typeName="submit"
                disableAction={watch("button_loader")}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
